import React, { useState } from 'react';
import { 
  DollarSign,
  Plus,
  Trash2,
  Edit3,
  Target,
  CheckCircle,
  Globe
} from 'lucide-react';

const DepositSettings = ({ 
  settings,
  handleSettingChange,
  currentCompany
}) => {
  
  // Deposit management state
  const [showAddDeposit, setShowAddDeposit] = useState(false);
  const [editingDeposit, setEditingDeposit] = useState(null);
  const [newDeposit, setNewDeposit] = useState({
    amount: '',
    currency: 'USD',
    symbol: '$'
  });

  // Available currencies
  const availableCurrencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'LKR', symbol: 'Rs', name: 'Sri Lankan Rupee' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' }
  ];

  // Deposit management functions
  const addDepositOption = () => {
    if (!newDeposit.amount || !newDeposit.currency) {
      alert('Please enter amount and select currency');
      return;
    }

    const selectedCurrency = availableCurrencies.find(c => c.code === newDeposit.currency);
    const newId = Math.max(...settings.depositOptions.map(d => d.id), 0) + 1;
    
    const depositOption = {
      id: newId,
      amount: newDeposit.amount,
      currency: newDeposit.currency,
      symbol: selectedCurrency.symbol,
      label: `${selectedCurrency.symbol}${newDeposit.amount}`,
      isDefault: settings.depositOptions.length === 0
    };

    handleSettingChange('depositOptions', [...settings.depositOptions, depositOption]);

    setNewDeposit({ amount: '', currency: 'USD', symbol: '$' });
    setShowAddDeposit(false);
  };

  const removeDepositOption = (depositId) => {
    if (settings.depositOptions.length <= 1) {
      alert('At least one deposit option is required');
      return;
    }

    const updatedOptions = settings.depositOptions.filter(d => d.id !== depositId);
    
    // If we removed the default, make the first one default
    if (settings.depositOptions.find(d => d.id === depositId)?.isDefault) {
      updatedOptions[0].isDefault = true;
    }

    handleSettingChange('depositOptions', updatedOptions);
  };

  const setDefaultDeposit = (depositId) => {
    const updatedOptions = settings.depositOptions.map(d => ({
      ...d,
      isDefault: d.id === depositId
    }));

    handleSettingChange('depositOptions', updatedOptions);
  };

  const editDepositOption = (deposit) => {
    setEditingDeposit(deposit);
    setNewDeposit({
      amount: deposit.amount,
      currency: deposit.currency,
      symbol: deposit.symbol
    });
    setShowAddDeposit(true);
  };

  const updateDepositOption = () => {
    if (!newDeposit.amount || !newDeposit.currency) {
      alert('Please enter amount and select currency');
      return;
    }

    const selectedCurrency = availableCurrencies.find(c => c.code === newDeposit.currency);
    
    const updatedOptions = settings.depositOptions.map(d => 
      d.id === editingDeposit.id 
        ? {
            ...d,
            amount: newDeposit.amount,
            currency: newDeposit.currency,
            symbol: selectedCurrency.symbol,
            label: `${selectedCurrency.symbol}${newDeposit.amount}`
          }
        : d
    );

    handleSettingChange('depositOptions', updatedOptions);

    setEditingDeposit(null);
    setNewDeposit({ amount: '', currency: 'USD', symbol: '$' });
    setShowAddDeposit(false);
  };

  const cancelDepositForm = () => {
    setShowAddDeposit(false);
    setEditingDeposit(null);
    setNewDeposit({ amount: '', currency: 'USD', symbol: '$' });
  };

  return (
    <div className="bg-gray-800 rounded-xl border border-gray-700 overflow-hidden">
      <div className="p-6 border-b border-gray-700 bg-gradient-to-r from-orange-900/30 to-red-900/30">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-semibold text-white flex items-center gap-3">
              <DollarSign className="w-6 h-6 text-orange-500" />
              Deposit Options
              <div className="flex items-center text-orange-400 text-xs">
                <Target size={12} className="mr-1" />
                <span>Multi-Currency</span>
              </div>
            </h3>
            <p className="text-gray-400 text-sm mt-1">
              Configure multiple deposit amounts with different currencies
            </p>
          </div>
          <button
            onClick={() => setShowAddDeposit(true)}
            className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-3 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
          >
            <Plus size={16} />
            <span>Add Deposit</span>
          </button>
        </div>
      </div>
      
      <div className="p-6">
        {/* Info Banner */}
        <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-3 mb-6 flex items-start gap-2">
          <Globe size={16} className="text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-blue-300 font-medium mb-1">Multi-Currency Deposit System</p>
            <p className="text-blue-200">
              Offer customers multiple deposit options in different currencies. Set one as default for new reservations.
            </p>
          </div>
        </div>

        {/* Existing Deposit Options */}
        <div className="space-y-3 mb-6">
          <h4 className="text-white font-medium flex items-center gap-2">
            <DollarSign size={16} className="text-orange-400" />
            Configured Deposit Options ({settings.depositOptions.length})
          </h4>
          
          {settings.depositOptions.map((deposit) => (
            <div key={deposit.id} className="flex items-center justify-between bg-gray-700/50 rounded-lg p-4 border border-gray-600 hover:border-orange-500/50 transition-colors">
              <div className="flex items-center space-x-4">
                <div className="flex items-center">
                  <input
                    type="radio"
                    name="defaultDeposit"
                    checked={deposit.isDefault}
                    onChange={() => setDefaultDeposit(deposit.id)}
                    className="mr-2 text-orange-500 cursor-pointer"
                    title="Set as default"
                  />
                  <span className="text-xs text-gray-400">Default</span>
                </div>
                
                <div className={`p-2 rounded-lg ${
                  deposit.isDefault 
                    ? 'bg-orange-500/20 border border-orange-500' 
                    : 'bg-gray-600/50 border border-gray-500'
                }`}>
                  <DollarSign size={20} className={deposit.isDefault ? 'text-orange-400' : 'text-gray-400'} />
                </div>
                
                <div>
                  <div className="text-white font-medium text-lg flex items-center gap-2">
                    {deposit.label}
                    {deposit.isDefault && (
                      <span className="text-xs bg-orange-900/30 text-orange-400 px-2 py-1 rounded-full border border-orange-700">
                        Default
                      </span>
                    )}
                  </div>
                  <div className="text-gray-400 text-sm">
                    {deposit.currency} - {availableCurrencies.find(c => c.code === deposit.currency)?.name}
                  </div>
                </div>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => editDepositOption(deposit)}
                  className="p-2 bg-gradient-to-r from-orange-500 to-red-600 text-white rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
                  title="Edit deposit option"
                >
                  <Edit3 size={14} />
                </button>
                {settings.depositOptions.length > 1 && (
                  <button
                    onClick={() => removeDepositOption(deposit.id)}
                    className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                    title="Delete deposit option"
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Add/Edit Deposit Form */}
        {showAddDeposit && (
          <div className="bg-gray-700/30 rounded-lg p-4 border border-orange-500/50">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-white font-medium flex items-center gap-2">
                <Plus size={16} className="text-orange-400" />
                {editingDeposit ? 'Edit Deposit Option' : 'Add New Deposit Option'}
              </h4>
              <button
                onClick={cancelDepositForm}
                className="text-gray-400 hover:text-white transition-colors"
              >
                ✕
              </button>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Deposit Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={newDeposit.amount}
                  onChange={(e) => setNewDeposit(prev => ({ ...prev, amount: e.target.value }))}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                  placeholder="100"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the deposit amount (numbers only)
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Currency *
                </label>
                <select
                  value={newDeposit.currency}
                  onChange={(e) => {
                    const selectedCurrency = availableCurrencies.find(c => c.code === e.target.value);
                    setNewDeposit(prev => ({ 
                      ...prev, 
                      currency: e.target.value,
                      symbol: selectedCurrency.symbol 
                    }));
                  }}
                  className="w-full p-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-orange-500"
                >
                  {availableCurrencies.map(currency => (
                    <option key={currency.code} value={currency.code}>
                      {currency.symbol} - {currency.name} ({currency.code})
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Select the currency for this deposit
                </p>
              </div>
            </div>

            {/* Preview */}
            {newDeposit.amount && (
              <div className="mt-4 bg-gray-800/50 rounded-lg p-3 border border-gray-600">
                <p className="text-xs text-gray-400 mb-1">Preview:</p>
                <p className="text-white font-medium text-lg">
                  {availableCurrencies.find(c => c.code === newDeposit.currency)?.symbol}{newDeposit.amount}
                </p>
                <p className="text-xs text-gray-400">
                  {availableCurrencies.find(c => c.code === newDeposit.currency)?.name}
                </p>
              </div>
            )}
            
            <div className="flex items-center gap-3 mt-4">
              <button
                onClick={editingDeposit ? updateDepositOption : addDepositOption}
                className="flex items-center gap-2 bg-gradient-to-r from-orange-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-orange-600 hover:to-red-700 transition-colors"
              >
                <CheckCircle size={16} />
                {editingDeposit ? 'Update' : 'Add'} Deposit
              </button>
              <button
                onClick={cancelDepositForm}
                className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Summary Section */}
        <div className="bg-gradient-to-r from-orange-500/10 to-red-500/10 border border-orange-500/30 rounded-lg p-4 mt-6">
          <h4 className="text-white font-medium mb-3 flex items-center gap-2">
            <CheckCircle size={16} className="text-orange-400" />
            Deposit Configuration Summary
          </h4>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
            <div>
              <span className="text-gray-400">Total Options:</span>
              <span className="text-white ml-2 font-medium">
                {settings.depositOptions.length}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Default:</span>
              <span className="text-orange-400 ml-2 font-medium">
                {settings.depositOptions.find(d => d.isDefault)?.label || 'None'}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Currencies:</span>
              <span className="text-white ml-2 font-medium">
                {[...new Set(settings.depositOptions.map(d => d.currency))].join(', ')}
              </span>
            </div>
          </div>
          
          {/* Quick Stats */}
          <div className="mt-3 pt-3 border-t border-orange-500/30">
            <div className="flex items-center gap-2 text-xs text-orange-300">
              <Globe size={12} />
              <span>
                Supporting {[...new Set(settings.depositOptions.map(d => d.currency))].length} different currencies
              </span>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-4 text-xs text-gray-500 bg-gray-700/30 rounded p-3 border border-gray-600">
          <p className="font-medium text-gray-400 mb-1">💡 Tips:</p>
          <ul className="list-disc list-inside space-y-1">
            <li>The default deposit will be pre-selected when creating new reservations</li>
            <li>You can offer multiple deposit amounts for customer flexibility</li>
            <li>Different currencies help serve international customers</li>
            <li>At least one deposit option is required</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DepositSettings;